import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authProvider } from '@/services/currentAuthProvider'
import type { MfaChallenge, MfaFactor, TotpEnrollmentInfo } from '@/services/AuthProvider'
import { getGlobalT } from '@/i18n'
import { initApiService } from '@/services/init'
import { extractApiErrorMessage } from '@/utils/errorHandling'

export interface MfaStatus {
  enabled: boolean
  canEnroll: boolean
  preferredMethod: 'totp' | 'sms' | null
  enrolledAt: number | null
  recoveryCodesRemaining: number
  mfaRequired: boolean
  gracePeriodExpired: boolean
}

export const useMfaStore = defineStore('mfa', () => {
  const t = getGlobalT()
  const mfaService = authProvider.mfa

  // State
  const status = ref<MfaStatus | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // TOTP enrollment state
  const enrollmentInProgress = ref(false)
  const totpEnrollmentInfo = ref<TotpEnrollmentInfo | null>(null)

  // MFA challenge state (during sign-in)
  const challengeResolver = ref<MfaChallenge | null>(null)
  const challengeHints = ref<MfaFactor[]>([])
  const challengeInProgress = ref(false)

  // Recovery codes
  const recoveryCodes = ref<string[] | null>(null)

  // Computed
  const isEnabled = computed(() => status.value?.enabled ?? false)
  const canEnroll = computed(() => status.value?.canEnroll ?? false)
  const isRequired = computed(() => status.value?.mfaRequired ?? false)
  const isGracePeriodExpired = computed(() => status.value?.gracePeriodExpired ?? false)
  const needsSetup = computed(() => isRequired.value && !isEnabled.value && isGracePeriodExpired.value)

  // Actions

  /**
   * Fetches the current MFA status from the backend.
   */
  async function fetchStatus() {
    loading.value = true
    error.value = null

    try {
      const apiService = initApiService()

      const response = await apiService.security.getMfaStatus()
      status.value = {
        enabled: response.data.enabled,
        canEnroll: response.data.canEnroll,
        preferredMethod: response.data.preferredMethod as 'totp' | 'sms' | null,
        enrolledAt: response.data.enrolledAt ?? null,
        recoveryCodesRemaining: response.data.recoveryCodesRemaining,
        mfaRequired: response.data.mfaRequired ?? false,
        gracePeriodExpired: response.data.gracePeriodExpired ?? false
      }
    } catch (e) {
      error.value = extractApiErrorMessage(e, t('errors.loadMfaStatus'))
      throw e
    } finally {
      loading.value = false
    }
  }

  /**
   * Starts TOTP enrollment.
   * Returns the enrollment info with QR code URL and secret.
   */
  async function startTotpEnrollment() {
    loading.value = true
    error.value = null
    enrollmentInProgress.value = true

    try {
      const info = await mfaService.startTotpEnrollment()
      totpEnrollmentInfo.value = info
      return info
    } catch (e) {
      error.value = extractApiErrorMessage(e, t('errors.startMfaEnrollment'))
      enrollmentInProgress.value = false
      throw e
    } finally {
      loading.value = false
    }
  }

  /**
   * Completes TOTP enrollment with the verification code.
   */
  async function completeTotpEnrollment(verificationCode: string) {
    if (!enrollmentInProgress.value) {
      throw new Error('No TOTP enrollment in progress')
    }

    loading.value = true
    error.value = null

    try {
      await mfaService.completeTotpEnrollment(verificationCode)

      // Record enrollment in backend
      await recordMfaEnrollment('totp')

      // Generate recovery codes
      await generateRecoveryCodes()

      // Clear enrollment state
      enrollmentInProgress.value = false
      totpEnrollmentInfo.value = null

      // Refresh status
      await fetchStatus()
    } catch (e) {
      error.value = extractApiErrorMessage(e, t('errors.completeMfaEnrollment'))
      throw e
    } finally {
      loading.value = false
    }
  }

  /**
   * Completes TOTP enrollment for contexts without a `users` table row
   * (e.g. admin portal). Only the Firebase-side enrollment is performed;
   * no backend recording or recovery-code generation happens.
   */
  async function completeTotpEnrollmentFirebaseOnly(verificationCode: string) {
    if (!enrollmentInProgress.value) {
      throw new Error('No TOTP enrollment in progress')
    }

    loading.value = true
    error.value = null

    try {
      await mfaService.completeTotpEnrollment(verificationCode)

      enrollmentInProgress.value = false
      totpEnrollmentInfo.value = null
    } catch (e) {
      error.value = extractApiErrorMessage(e, t('errors.completeMfaEnrollment'))
      throw e
    } finally {
      loading.value = false
    }
  }

  /**
   * Records MFA enrollment in the backend.
   */
  async function recordMfaEnrollment(method: 'totp' | 'sms') {
    const apiService = initApiService()

    await apiService.security.recordMfaEnrollment({ method })
  }

  /**
   * Generates new recovery codes.
   */
  async function generateRecoveryCodes() {
    loading.value = true
    error.value = null

    try {
      const apiService = initApiService()

      const response = await apiService.security.generateRecoveryCodes()
      recoveryCodes.value = response.data.codes

      // Refresh status to update recovery codes count
      await fetchStatus()

      return response.data.codes
    } catch (e) {
      error.value = extractApiErrorMessage(e, t('errors.generateRecoveryCodes'))
      throw e
    } finally {
      loading.value = false
    }
  }

  /**
   * Disables MFA for the current user.
   */
  async function disableMfa() {
    loading.value = true
    error.value = null

    try {
      // Unenroll all factors from Firebase
      await mfaService.unenrollAllFactors()

      // Record in backend
      const apiService = initApiService()

      await apiService.security.recordMfaDisabled()

      // Clear recovery codes
      recoveryCodes.value = null

      // Refresh status
      await fetchStatus()
    } catch (e) {
      error.value = extractApiErrorMessage(e, t('mfa.settings.disableFailed'))
      throw e
    } finally {
      loading.value = false
    }
  }

  /**
   * Sets up MFA challenge for sign-in.
   */
  function setChallenge(resolver: MfaChallenge) {
    challengeResolver.value = resolver
    challengeHints.value = [...resolver.hints]
    challengeInProgress.value = true
  }

  /**
   * Handles TOTP challenge during sign-in.
   */
  async function handleTotpChallenge(verificationCode: string) {
    if (!challengeResolver.value) {
      throw new Error('No MFA challenge in progress')
    }

    loading.value = true
    error.value = null

    try {
      await mfaService.handleTotpChallenge(
        challengeResolver.value,
        0, // Use first factor
        verificationCode
      )

      clearChallenge()
    } catch (e) {
      error.value = extractApiErrorMessage(e, t('errors.verifyMfaCode'))
      throw e
    } finally {
      loading.value = false
    }
  }

  /**
   * Clears the MFA challenge state.
   */
  function clearChallenge() {
    challengeResolver.value = null
    challengeHints.value = []
    challengeInProgress.value = false
  }

  /**
   * Clears the recovery codes from state.
   * Should be called after user has saved their codes.
   */
  function clearRecoveryCodes() {
    recoveryCodes.value = null
  }

  /**
   * Cancels enrollment in progress.
   */
  function cancelEnrollment() {
    enrollmentInProgress.value = false
    totpEnrollmentInfo.value = null
    mfaService.clearPendingEnrollment()
    error.value = null
  }

  return {
    // State
    status,
    loading,
    error,
    enrollmentInProgress,
    totpEnrollmentInfo,
    challengeResolver,
    challengeHints,
    challengeInProgress,
    recoveryCodes,

    // Computed
    isEnabled,
    canEnroll,
    isRequired,
    isGracePeriodExpired,
    needsSetup,

    // Actions
    fetchStatus,
    startTotpEnrollment,
    completeTotpEnrollment,
    completeTotpEnrollmentFirebaseOnly,
    generateRecoveryCodes,
    disableMfa,
    setChallenge,
    handleTotpChallenge,
    clearChallenge,
    clearRecoveryCodes,
    cancelEnrollment
  }
})
